import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function LoanEMIDashboard() {
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, due_today, due_this_week, overdue, npa
  const [searchTerm, setSearchTerm] = useState('');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedReminderType, setSelectedReminderType] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('payment'); // 'payment' or 'settle'
  const [settlementMode, setSettlementMode] = useState('complete'); // complete | recovery
  const [settlementAmount, setSettlementAmount] = useState('');
  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().split('T')[0]);
  const [settlementNotes, setSettlementNotes] = useState('');
  const [settlementLoading, setSettlementLoading] = useState(false);
  const navigate = useNavigate();
  const NPA_DAYS = 50;

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch only active loans from the backend
      const response = await axios.get(`${config.API_URL}/enhanced-loans/?status=active`);
      const activeLoans = Array.isArray(response.data) ? response.data : [];
      
      // Calculate next due date and payment details for each loan
      const loansWithDues = activeLoans.map(loan => {
        const payments = loan.payments || [];
        const completedPayments = payments.filter(p => p.status === 'completed').length;
        const totalPaid = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0);
        const pendingPayments = payments.filter(p => p.status === 'pending' || p.status === 'overdue');
        const nextPayment = pendingPayments[0];
        
        // Calculate late fee (0.5% per day on disbursed amount)
        let lateFee = 0;
        let daysLate = 0;
        if (nextPayment && nextPayment.dueDate) {
          const dueDate = new Date(nextPayment.dueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dueDate.setHours(0, 0, 0, 0);
          
          const timeDiff = today.getTime() - dueDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          
          if (daysDiff > 0) {
            daysLate = daysDiff;
            lateFee = (loan.loanAmount * 0.005) * daysLate; // 0.5% per day
          }
        }

        // Add unpaid late fees from loan.lateFees
        if (loan.lateFees && loan.lateFees.length > 0) {
          const unpaidLateFees = loan.lateFees.filter(fee => !fee.isPaid);
          unpaidLateFees.forEach(fee => {
            lateFee += fee.amount || 0;
          });
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextDueDate = nextPayment ? new Date(nextPayment.dueDate) : null;
        if (nextDueDate) {
          nextDueDate.setHours(0, 0, 0, 0);
        }

        const timeDiff = nextDueDate ? (nextDueDate.getTime() - today.getTime()) : 0;
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        
        // No next installment → not "due today" (was a bug: daysDiff=0 when nextDueDate null)
        const scheduleDone =
          !nextPayment &&
          (completedPayments >= (loan.tenure || 0) ||
            payments.length > 0 && payments.every((p) => p.status === 'completed' || p.isLateFee));
        const isOverdue = Boolean(nextPayment && daysDiff < 0);
        const daysLateAbs = isOverdue ? Math.abs(daysDiff) : daysLate;
        const isNpa = Boolean(loan.isNpa) || (isOverdue && daysLateAbs >= NPA_DAYS);
        
        return {
          ...loan,
          totalPaid,
          remainingAmount: Math.max(0, (loan.totalAmount || 0) - totalPaid + lateFee),
          completedPayments,
          totalInstallments: loan.tenure || payments.length,
          nextDueDate: nextDueDate,
          daysDiff: nextPayment ? daysDiff : null,
          lateFee: Math.round(lateFee * 100) / 100,
          daysLate: daysLateAbs,
          isOverdue,
          isDueToday: Boolean(nextPayment && daysDiff === 0),
          isDueThisWeek: Boolean(nextPayment && daysDiff >= 0 && daysDiff <= 7),
          isNpa,
          scheduleDone,
          nextPayment: nextPayment
        };
      });
      
      // Apply due filter
      let filteredLoans = loansWithDues;
      if (filter === 'due_today') {
        filteredLoans = loansWithDues.filter(loan => loan.isDueToday);
      } else if (filter === 'due_this_week') {
        filteredLoans = loansWithDues.filter(loan => loan.isDueThisWeek);
      } else if (filter === 'overdue') {
        filteredLoans = loansWithDues.filter(loan => loan.isOverdue && !loan.isNpa);
      } else if (filter === 'npa') {
        filteredLoans = loansWithDues.filter(loan => loan.isNpa);
      }
      
      // Sort by due date (most urgent first)
      filteredLoans.sort((a, b) => {
        if (!a.nextDueDate) return 1;
        if (!b.nextDueDate) return -1;
        return a.nextDueDate.getTime() - b.nextDueDate.getTime();
      });
      
      setLoans(loansWithDues);
      setFilteredLoans(filteredLoans);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching loan dashboard data:', err);
      setError('Failed to load loan dashboard data');
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Real-time search effect
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLoans(loans);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = loans.filter(loan => {
      const customerName = loan.customer?.name || loan.name || '';
      const customerPhone = loan.customer?.phone || loan.phone || '';
      return customerPhone.includes(searchTerm) || 
             customerName.toLowerCase().includes(searchLower);
    });
    
    setFilteredLoans(filtered);
  }, [searchTerm, loans]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Get status color and text
  const getStatusInfo = (loan) => {
    if (loan.scheduleDone || !loan.nextPayment) {
      return {
        color: 'success',
        text: 'EMI schedule complete',
        bgColor: 'bg-success',
        textColor: 'text-white',
        badge: 'PAID / CLOSE',
      };
    }
    if (loan.isNpa) {
      return {
        color: 'dark',
        text: `NPA · ${Math.abs(loan.daysDiff || loan.daysLate || 0)} दिन`,
        bgColor: 'bg-dark',
        textColor: 'text-white',
        badge: 'NPA',
      };
    }
    if (loan.isOverdue) {
      return {
        color: 'danger',
        text: `${Math.abs(loan.daysDiff)} दिन देर`,
        bgColor: 'bg-danger',
        textColor: 'text-white',
        badge: 'OVERDUE',
      };
    }
    if (loan.isDueToday) {
      return {
        color: 'warning',
        text: 'आज due है',
        bgColor: 'bg-warning',
        textColor: 'text-dark',
        badge: 'DUE TODAY',
      };
    }
    if (loan.isDueThisWeek) {
      return {
        color: 'info',
        text: `${loan.daysDiff} दिन बाकी`,
        bgColor: 'bg-info',
        textColor: 'text-white',
        badge: 'ACTIVE',
      };
    }
    return {
      color: 'success',
      text: loan.daysDiff != null ? `${loan.daysDiff} दिन बाकी` : 'Active',
      bgColor: 'bg-light',
      textColor: 'text-dark',
      badge: 'ACTIVE',
    };
  };

  // Quick payment handler - Open payment modal
  const handleQuickPayment = (loan) => {
    setSelectedLoan(loan);
    // Calculate recommended payment amount (EMI + late fee if any)
    const recommendedAmount = loan.monthlyInstallment + (loan.lateFee || 0);
    setPaymentAmount(recommendedAmount.toFixed(2));
    setPaymentDate(new Date().toISOString().split('T')[0]);
    // Calculate total due for settlement
    const totalDue = loan.totalAmount || loan.loanAmount || 0;
    const totalLateFees = loan.lateFee || 0;
    const totalDueWithFees = totalDue + totalLateFees;
    const totalPaid = loan.totalPaid || 0;
    const remaining = totalDueWithFees - totalPaid;
    setSettlementAmount(Math.max(0, remaining).toFixed(2));
    setSettlementDate(new Date().toISOString().split('T')[0]);
    setSettlementNotes('');
    setActiveTab('payment');
    setShowPaymentModal(true);
  };

  // Handle payment recording
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    // Round amount properly before validation
    let roundedAmount = parseFloat(paymentAmount);
    if (!isNaN(roundedAmount) && roundedAmount > 0) {
      const decimalPart = roundedAmount % 1;
      if (decimalPart > 0) {
        roundedAmount = Math.ceil(roundedAmount);
      } else {
        roundedAmount = Math.round(roundedAmount * 100) / 100;
      }
    }
    
    if (!selectedLoan || !paymentAmount || roundedAmount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    // Allow flexible payment - allow small variations (up to 10% more or less)
    const totalDue = selectedLoan.remainingAmount + (selectedLoan.lateFee || 0);
    const tolerance = Math.max(10, Math.ceil(totalDue * 0.1)); // Allow up to 10% or minimum ₹10 tolerance
    const maxAllowed = totalDue + tolerance;
    const minAllowed = Math.max(0, totalDue - tolerance);
    
    if (roundedAmount > maxAllowed) {
      alert(`Payment amount cannot exceed ₹${maxAllowed.toFixed(2)}. Remaining amount is ₹${totalDue.toFixed(2)}.`);
      return;
    }
    
    // Note: Payment less than scheduled EMI will redistribute to remaining EMIs automatically

    try {
      setPaymentLoading(true);
      setError(null);

      // Record payment via enhanced-loans endpoint
      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/payments`,
        {
          amount: roundedAmount,
          paymentDate: paymentDate,
          paymentMethod: paymentMethod,
          receiptNumber: `REC-${Date.now()}`,
          notes: `EMI payment recorded from Loan EMI Dashboard`
        }
      );

      if (response.data.success) {
        alert('Payment recorded successfully! WhatsApp confirmation sent to customer.');
        setShowPaymentModal(false);
        setSelectedLoan(null);
        setPaymentAmount('');
        // Refresh dashboard data
        fetchDashboardData();
      } else {
        throw new Error(response.data.message || 'Payment failed');
      }
      
    } catch (err) {
      console.error('Error recording payment:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to record payment';
      alert('Error: ' + errorMsg);
      setError(errorMsg);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle loan settlement
  const handleSettleLoan = async (e) => {
    e.preventDefault();
    
    if (!selectedLoan || !settlementAmount || parseFloat(settlementAmount) < 0) {
      alert('Please enter a valid settlement amount');
      return;
    }

    const amt = parseFloat(settlementAmount);
    const confirmSettle = window.confirm(
      settlementMode === 'recovery'
        ? `NPA Recovery record karein?\n\nCustomer: ${selectedLoan.customer?.name || selectedLoan.name || 'N/A'}\nRecovery: ₹${amt.toLocaleString('en-IN')}\n\nLoan active rahegi jab tak baaki clear na ho.`
        : `Complete Settle karein?\n\nCustomer: ${selectedLoan.customer?.name || selectedLoan.name || 'N/A'}\nSettlement: ₹${amt.toLocaleString('en-IN')}\n\nCase CLOSE — recovery band. Loss par customer Risky mark hoga.`
    );

    if (!confirmSettle) {
      return;
    }

    try {
      setSettlementLoading(true);
      setError(null);

      // Settle loan via enhanced-loans endpoint
      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${selectedLoan._id}/settle`,
        {
          settlementAmount: parseFloat(settlementAmount),
          settlementDate: settlementDate,
          notes: settlementNotes,
          mode: settlementMode,
        }
      );

      if (response.data.success) {
        const details = response.data.settlementDetails;
        const profitLossType = details.profitLossType === 'profit' ? 'Profit' : 'Loss';
        alert(
          `Loan settled successfully!\n\n` +
          `Final Amount: ₹${details.finalAmount.toLocaleString('en-IN')}\n` +
          `Total Due: ₹${details.totalDue.toLocaleString('en-IN')}\n` +
          `${profitLossType}: ₹${Math.abs(details.profitLoss).toLocaleString('en-IN')}`
        );
        setShowPaymentModal(false);
        setSelectedLoan(null);
        setSettlementAmount('');
        setSettlementNotes('');
        // Refresh dashboard data
        fetchDashboardData();
      } else {
        throw new Error(response.data.message || 'Settlement failed');
      }
      
    } catch (err) {
      console.error('Error settling loan:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to settle loan';
      alert('Error: ' + errorMsg);
      setError(errorMsg);
    } finally {
      setSettlementLoading(false);
    }
  };

  // Get summary stats
  const getSummaryStats = () => {
    const currentLoans = searchTerm ? filteredLoans : loans;
    const totalActive = currentLoans.length;
    const npa = currentLoans.filter((loan) => loan.isNpa).length;
    const overdue = currentLoans.filter((loan) => loan.isOverdue && !loan.isNpa).length;
    const dueToday = currentLoans.filter((loan) => loan.isDueToday).length;
    const dueThisWeek = currentLoans.filter((loan) => loan.isDueThisWeek).length;
    const totalLateFees = currentLoans.reduce((sum, loan) => sum + (loan.lateFee || 0), 0);
    const totalDisbursed = currentLoans.reduce((sum, loan) => sum + (loan.loanAmount || 0), 0);
    const totalCollected = currentLoans.reduce((sum, loan) => sum + (loan.totalPaid || 0), 0);
    
    return { totalActive, overdue, npa, dueToday, dueThisWeek, totalLateFees, totalDisbursed, totalCollected };
  };

  const stats = getSummaryStats();

  const handleSendReminders = async () => {
    if (!selectedReminderType) {
      alert('Please select a reminder type');
      return;
    }
    
    setSendingReminders(true);
    setReminderResult(null);
    
    try {
      const response = await axios.post(`${config.API_URL}/enhanced-loans/send-reminders`, {
        reminderType: selectedReminderType
      });
      
      setReminderResult(response.data);
      setShowReminderModal(false);
      
      // Refresh the dashboard data
      await fetchDashboardData();
      
    } catch (error) {
      console.error('Error sending reminders:', error);
      setReminderResult({
        success: false,
        message: error.response?.data?.message || 'Failed to send reminders'
      });
    } finally {
      setSendingReminders(false);
    }
  };

  const getReminderTypeLabel = (type) => {
    switch (type) {
      case 'overdue':
        return 'Overdue Customers';
      case 'due_today':
        return 'Due Today Customers';
      case 'due_this_week':
        return 'Due This Week Customers';
      default:
        return type;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <button className="btn btn-danger" onClick={fetchDashboardData}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0 d-flex align-items-center">
          <i className="bi bi-bank me-2 fs-2"></i> Loan EMI Dashboard
        </h2>
        <div>
          <Link className="btn btn-primary" to="/loan-management">
            <i className="bi bi-bank me-1"></i> Loan Management
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">Total Active Loans</h5>
              <h3>{stats.totalActive}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <h5 className="card-title">⚠️ Overdue</h5>
              <h3>{stats.overdue}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-warning text-dark">
            <div className="card-body">
              <h5 className="card-title">📅 Due Today</h5>
              <h3>{stats.dueToday}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">📆 Due This Week</h5>
              <h3>{stats.dueThisWeek}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5 className="card-title">Total Disbursed</h5>
              <h4>{formatCurrency(stats.totalDisbursed)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card bg-secondary text-white">
            <div className="card-body">
              <h5 className="card-title">Total Collected</h5>
              <h4>{formatCurrency(stats.totalCollected)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <h5 className="card-title">Total Late Fees</h5>
              <h4>{formatCurrency(stats.totalLateFees)}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Late Fees Alert */}
      {stats.totalLateFees > 0 && (
        <div className="alert alert-warning mb-4">
          <h5>⚠️ Total Late Fees: {formatCurrency(stats.totalLateFees)}</h5>
          <small>0.5% of disbursed amount per day की दर से late fee बढ़ रही है</small>
        </div>
      )}

      {/* Search Box */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search customer by phone or name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button"
                    onClick={clearSearch}
                    title="Clear search"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
              <div className="form-text small">
                Type to search customers instantly
              </div>
            </div>
            <div className="col-md-6 text-end">
              {searchTerm && (
                <span className="badge bg-info">
                  {filteredLoans.length} results found
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label">Filter by Due Status</label>
              <div className="btn-group w-100" role="group">
                <button
                  type="button"
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All ({loans.length})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'overdue' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setFilter('overdue')}
                >
                  Overdue ({stats.overdue})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'npa' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setFilter('npa')}
                  title={`${NPA_DAYS}+ din overdue — recovery / settle`}
                >
                  NPA ({stats.npa})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'due_today' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('due_today')}
                >
                  Due Today ({stats.dueToday})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'due_this_week' ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => setFilter('due_this_week')}
                >
                  Due This Week ({stats.dueThisWeek})
                </button>
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Send Reminders</label>
              <button
                className="btn btn-success w-100"
                onClick={() => setShowReminderModal(true)}
              >
                <i className="bi bi-bell me-1"></i> Send Reminder Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loans List */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Customer</th>
                  <th>Loan Details</th>
                  <th>Next Due Date</th>
                  <th>Monthly EMI</th>
                  <th>Remaining Amount</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLoans.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <p className="text-muted mb-0">
                        {searchTerm 
                          ? `No customers found matching "${searchTerm}"` 
                          : "No active loans match the selected filter."
                        }
                      </p>
                      <p className="text-muted mb-0 small">
                        {searchTerm 
                          ? "Try typing a different name or phone number" 
                          : "Try approving a new loan from the Loan Management page."
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredLoans.map(loan => {
                    const statusInfo = getStatusInfo(loan);
                    const progressPercent = loan.totalAmount > 0 
                      ? (loan.totalPaid / loan.totalAmount) * 100 
                      : 0;
                    
                    return (
                      <tr
                        key={loan._id}
                        className={
                          loan.isNpa
                            ? 'table-secondary'
                            : loan.isOverdue
                              ? 'table-danger'
                              : loan.isDueToday
                                ? 'table-warning'
                                : ''
                        }
                      >
                        <td>
                          <strong>{loan.customer?.name || loan.name || 'N/A'}</strong><br />
                          <small className="text-muted">{loan.customer?.phone || loan.phone || 'N/A'}</small>
                        </td>
                        <td>
                          <strong>{loan.loanType || 'Personal Loan'}</strong><br />
                          <small className="text-muted">{loan.loanPurpose || loan.purpose || 'N/A'}</small><br />
                          <small className="text-muted">Disbursed: {formatCurrency(loan.loanAmount)}</small>
                        </td>
                        <td>
                          {loan.nextDueDate ? (
                            <>
                              <strong>{formatDate(loan.nextDueDate)}</strong><br />
                              <small className={`badge ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                                {statusInfo.text}
                              </small>
                            </>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <strong>{formatCurrency(loan.monthlyInstallment)}</strong>
                          {loan.lateFee > 0 && (
                            <div className="text-danger small">
                              + {formatCurrency(loan.lateFee)} late fee
                            </div>
                          )}
                        </td>
                        <td>
                          <strong>{formatCurrency(loan.remainingAmount)}</strong><br />
                          <small className="text-muted">
                            Paid: {formatCurrency(loan.totalPaid)}
                          </small>
                        </td>
                        <td>
                          <div className="progress" style={{height: '20px'}}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{width: `${progressPercent}%`}}
                              aria-valuenow={progressPercent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            >
                              {loan.completedPayments}/{loan.totalInstallments} EMIs
                            </div>
                          </div>
                          <small className="text-muted">{Math.round(progressPercent)}% paid</small>
                        </td>
                        <td>
                          <span className={`badge bg-${statusInfo.color}`}>
                            {statusInfo.badge || (loan.isOverdue ? 'OVERDUE' : loan.isDueToday ? 'DUE TODAY' : 'ACTIVE')}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            {loan.isNpa && (
                              <button
                                type="button"
                                className="btn btn-sm btn-dark"
                                title="NPA Recovery"
                                onClick={() => {
                                  handleQuickPayment(loan);
                                  setSettlementMode('recovery');
                                  setActiveTab('settle');
                                  setSettlementAmount('');
                                }}
                              >
                                Recovery
                              </button>
                            )}
                            <button
                              onClick={() => {
                                handleQuickPayment(loan);
                                if (loan.isNpa) {
                                  setSettlementMode('complete');
                                }
                              }}
                              className="btn btn-sm btn-success"
                              title="Record Payment / Settle"
                            >
                              <i className="bi bi-currency-rupee me-1"></i> Pay
                            </button>
                            <Link
                              to={`/loan-management`}
                              className="btn btn-sm btn-outline-primary"
                              title="View Details"
                            >
                              <i className="bi bi-eye"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedLoan && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-currency-rupee me-2"></i>
                  {activeTab === 'payment' ? 'Record EMI Payment' : 'Settle Loan'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedLoan(null);
                    setPaymentAmount('');
                    setSettlementAmount('');
                    setSettlementNotes('');
                    setError(null);
                    setActiveTab('payment');
                  }}
                ></button>
              </div>
              
              {/* Tabs */}
              <ul className="nav nav-tabs mx-3 mt-2" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`}
                    onClick={() => setActiveTab('payment')}
                    type="button"
                  >
                    <i className="bi bi-credit-card me-1"></i> Payment
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'settle' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settle')}
                    type="button"
                  >
                    <i className="bi bi-check-circle me-1"></i> Settle Loan
                  </button>
                </li>
              </ul>

              {/* Tab Content - Payment */}
              {activeTab === 'payment' && (
              <form onSubmit={handleRecordPayment}>
                <div className="modal-body">
                  {/* Loan Information */}
                  <div className="alert alert-info">
                    <strong>Customer:</strong> {selectedLoan.customer?.name || selectedLoan.name || 'N/A'}<br />
                    <strong>Phone:</strong> {selectedLoan.customer?.phone || selectedLoan.phone || 'N/A'}<br />
                    <strong>Loan ID:</strong> {selectedLoan.applicationId || selectedLoan._id?.slice(-8) || 'N/A'}<br />
                    <strong>Monthly EMI:</strong> {formatCurrency(selectedLoan.monthlyInstallment)}
                    {selectedLoan.lateFee > 0 && (
                      <>
                        <br />
                        <strong className="text-danger">Late Fee:</strong> {formatCurrency(selectedLoan.lateFee)}
                      </>
                    )}
                  </div>

                  {error && (
                    <div className="alert alert-danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>{error}
                    </div>
                  )}

                  {/* Payment Amount */}
                  <div className="mb-3">
                    <label htmlFor="paymentAmount" className="form-label">
                      <strong>Payment Amount (₹)</strong>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      id="paymentAmount"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      min="0"
                      max={Math.ceil((selectedLoan.remainingAmount + (selectedLoan.lateFee || 0) + Math.max(10, Math.ceil((selectedLoan.remainingAmount + (selectedLoan.lateFee || 0)) * 0.1))) * 100) / 100}
                      step="0.01"
                      required
                      autoFocus
                    />
                    <div className="form-text">
                      Recommended: {formatCurrency(selectedLoan.monthlyInstallment + (selectedLoan.lateFee || 0))}
                      {selectedLoan.lateFee > 0 && (
                        <span className="text-danger">
                          {' '}(EMI: {formatCurrency(selectedLoan.monthlyInstallment)} + Late Fee: {formatCurrency(selectedLoan.lateFee)})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Payment Date */}
                  <div className="mb-3">
                    <label htmlFor="paymentDate" className="form-label">
                      <strong>Payment Date</strong>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="paymentDate"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      required
                    />
                  </div>

                  {/* Payment Method */}
                  <div className="mb-3">
                    <label htmlFor="paymentMethod" className="form-label">
                      <strong>Payment Method</strong>
                    </label>
                    <select
                      className="form-select"
                      id="paymentMethod"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      required
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="self_finance">Self Finance</option>
                    </select>
                  </div>

                  {/* Payment Summary */}
                  <div className="card bg-light">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6">
                          <small className="text-muted">Remaining Amount:</small>
                          <div className="h5">{formatCurrency(selectedLoan.remainingAmount)}</div>
                        </div>
                        <div className="col-6 text-end">
                          <small className="text-muted">After Payment:</small>
                          <div className="h5 text-success">
                            {formatCurrency(Math.max(0, selectedLoan.remainingAmount - parseFloat(paymentAmount || 0)))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp Message Note */}
                  <div className="alert alert-success mb-0">
                    <i className="bi bi-whatsapp me-2"></i>
                    <small>WhatsApp confirmation message will be sent automatically to the customer after payment is recorded.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedLoan(null);
                      setPaymentAmount('');
                      setError(null);
                      setActiveTab('payment');
                    }}
                    disabled={paymentLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={paymentLoading || !paymentAmount || parseFloat(paymentAmount) <= 0}
                  >
                    {paymentLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Record Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
              )}

              {/* Tab Content - Settle Loan */}
              {activeTab === 'settle' && (
                <form onSubmit={handleSettleLoan}>
                <div className="modal-body">
                  <div className="btn-group w-100 mb-3" role="group">
                    <button
                      type="button"
                      className={`btn ${settlementMode === 'recovery' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => {
                        setSettlementMode('recovery');
                        setSettlementAmount('');
                      }}
                    >
                      Recovery (NPA)
                    </button>
                    <button
                      type="button"
                      className={`btn ${settlementMode === 'complete' ? 'btn-dark' : 'btn-outline-dark'}`}
                      onClick={() => {
                        setSettlementMode('complete');
                        setSettlementAmount(
                          Math.max(0, selectedLoan.remainingAmount || 0).toFixed(2)
                        );
                      }}
                    >
                      Complete Settle
                    </button>
                  </div>
                  <div className={`alert small ${settlementMode === 'recovery' ? 'alert-success' : 'alert-warning'}`}>
                    {settlementMode === 'recovery'
                      ? `Recovery = jitna cash aaya record karo. Loan active rahegi. NPA threshold: ${NPA_DAYS}+ din overdue.`
                      : 'Complete Settle = case close. ₹0 = full write-off. Loss par customer Risky mark hoga.'}
                  </div>
                  {/* Loan Information */}
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Warning:</strong> Settling a loan will mark it as COMPLETED. This action cannot be undone.
                  </div>

                  <div className="alert alert-info">
                    <strong>Customer:</strong> {selectedLoan.customer?.name || selectedLoan.name || 'N/A'}<br />
                    <strong>Phone:</strong> {selectedLoan.customer?.phone || selectedLoan.phone || 'N/A'}<br />
                    <strong>Loan ID:</strong> {selectedLoan.applicationId || selectedLoan._id?.slice(-8) || 'N/A'}<br />
                    <strong>Total Loan Amount:</strong> {formatCurrency(selectedLoan.totalAmount || selectedLoan.loanAmount || 0)}<br />
                    <strong>Already Paid:</strong> {formatCurrency(selectedLoan.totalPaid || 0)}<br />
                    <strong>Late Fees:</strong> {formatCurrency(selectedLoan.lateFee || 0)}<br />
                    <strong className="text-danger">Total Due (Remaining):</strong> {formatCurrency(selectedLoan.remainingAmount || 0)}
                  </div>

                  {error && (
                    <div className="alert alert-danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>{error}
                    </div>
                  )}

                  {/* Settlement Amount */}
                  <div className="mb-3">
                    <label htmlFor="settlementAmount" className="form-label">
                      <strong>Final Settlement Amount (₹)</strong>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      id="settlementAmount"
                      value={settlementAmount}
                      onChange={(e) => setSettlementAmount(e.target.value)}
                      min="0"
                      step="0.01"
                      required
                      autoFocus
                    />
                    <div className="form-text">
                      Enter the final amount company will receive. This will calculate profit/loss automatically.
                    </div>
                  </div>

                  {/* Settlement Date */}
                  <div className="mb-3">
                    <label htmlFor="settlementDate" className="form-label">
                      <strong>Settlement Date</strong>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="settlementDate"
                      value={settlementDate}
                      onChange={(e) => setSettlementDate(e.target.value)}
                      required
                    />
                  </div>

                  {/* Settlement Notes */}
                  <div className="mb-3">
                    <label htmlFor="settlementNotes" className="form-label">
                      <strong>Notes (Optional)</strong>
                    </label>
                    <textarea
                      className="form-control"
                      id="settlementNotes"
                      rows="3"
                      value={settlementNotes}
                      onChange={(e) => setSettlementNotes(e.target.value)}
                      placeholder="Reason for settlement, payment terms, etc."
                    ></textarea>
                  </div>

                  {/* Profit/Loss Preview */}
                  {settlementAmount && parseFloat(settlementAmount) >= 0 && (
                    <div className="card">
                      <div className="card-body">
                        <h6 className="card-title">Settlement Summary</h6>
                        <div className="row">
                          <div className="col-6">
                            <small className="text-muted">Remaining Due:</small>
                            <div className="h6">{formatCurrency(selectedLoan.remainingAmount || 0)}</div>
                          </div>
                          <div className="col-6">
                            <small className="text-muted">Settlement Amount:</small>
                            <div className="h6">{formatCurrency(parseFloat(settlementAmount || 0))}</div>
                          </div>
                        </div>
                        <hr />
                        <div className="row">
                          <div className="col-12">
                            <small className="text-muted">Profit / Loss:</small>
                            <div className={`h5 ${((parseFloat(settlementAmount || 0) - (selectedLoan.remainingAmount || 0)) >= 0) ? 'text-success' : 'text-danger'}`}>
                              {formatCurrency(parseFloat(settlementAmount || 0) - (selectedLoan.remainingAmount || 0))}
                            </div>
                            <small className="text-muted">
                              (Settlement Amount - Remaining Due)
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedLoan(null);
                      setSettlementAmount('');
                      setSettlementNotes('');
                      setError(null);
                      setActiveTab('payment');
                    }}
                    disabled={settlementLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-warning"
                    disabled={settlementLoading || !settlementAmount || parseFloat(settlementAmount) < 0}
                  >
                    {settlementLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Settling...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Settle Loan
                      </>
                    )}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reminder Result Alert */}
      {reminderResult && (
        <div className={`alert ${reminderResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
          <h5>{reminderResult.success ? '✅ Reminders Sent Successfully!' : '❌ Failed to Send Reminders'}</h5>
          <p>{reminderResult.message}</p>
          {reminderResult.summary && (
            <div className="mt-2">
              <strong>Summary:</strong>
              <ul className="mb-0 mt-1">
                <li>Total Targeted: {reminderResult.summary.totalTargeted}</li>
                <li>Successfully Sent: {reminderResult.summary.sent}</li>
                <li>Failed: {reminderResult.summary.failed}</li>
                <li>Type: {getReminderTypeLabel(reminderResult.summary.reminderType)}</li>
              </ul>
            </div>
          )}
          <button 
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setReminderResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Send Reminder Modal */}
      {showReminderModal && (
        <div className="modal fade show" style={{display: 'block'}} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-bell me-2"></i>
                  Send Loan Payment Reminders
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowReminderModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  Select which customers you want to send reminders to:
                </p>
                
                <div className="mb-3">
                  <label className="form-label">Reminder Type:</label>
                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className={`btn ${selectedReminderType === 'overdue' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setSelectedReminderType('overdue')}
                    >
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Overdue Customers ({stats.overdue})
                    </button>
                    <button
                      type="button"
                      className={`btn ${selectedReminderType === 'due_today' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setSelectedReminderType('due_today')}
                    >
                      <i className="bi bi-calendar-day me-2"></i>
                      Due Today ({stats.dueToday})
                    </button>
                    <button
                      type="button"
                      className={`btn ${selectedReminderType === 'due_this_week' ? 'btn-info' : 'btn-outline-info'}`}
                      onClick={() => setSelectedReminderType('due_this_week')}
                    >
                      <i className="bi bi-calendar-week me-2"></i>
                      Due This Week ({stats.dueThisWeek})
                    </button>
                  </div>
                </div>
                
                {selectedReminderType && (
                  <div className="alert alert-info">
                    <strong>Selected:</strong> {getReminderTypeLabel(selectedReminderType)}
                    <br />
                    <small>This will send WhatsApp reminders to all customers in this category.</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowReminderModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleSendReminders}
                  disabled={!selectedReminderType || sendingReminders}
                >
                  {sendingReminders ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>
                      Send Reminders
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showReminderModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default LoanEMIDashboard;

